import React, { ReactNode, useCallback, useMemo } from 'react';
import EChartsWithData from '../../../component/chart/echarts/EChartsWithData';
import { tooltipRow, wrapTooltip } from '../../../component/chart/echarts/tooltipAdapter';
import { EChartPoint, EChartThreshold, EChartTimeSeries, EChartYAxis } from '../../../component/chart/echarts/types';
import { CHART_DATA_TYPE } from '../../../component/Constants';

export interface SelfMonitorSeries {
    name: string;
    data: [number, number][];
    color?: string;
    dashStyle?: string;
    chartType?: 'line' | 'bar';
    stack?: string;
}

export interface SelfMonitorPlotLine {
    value: number;
    label: string;
    color?: string;
}

interface SelfMonitorChartProps {
    title: string;
    icon: string;
    series: SelfMonitorSeries[];
    yAxisLabel: string;
    valueSuffix?: string;
    plotLines?: SelfMonitorPlotLine[];
    note?: string;
    height?: number;
    loading: boolean;
    headerActions?: ReactNode;
}

const DEFAULT_LIMIT_COLOR = '#dc3545';

// Shared time-series chart for the Overview tab. The x-axis is fitted to the
// data rather than the globally-selected time range, and range selection is
// off - the live buffer is a fixed-cadence, short (<=60 minute) window, so
// snapping to or brushing the global range would fight it.
const SelfMonitorChart: React.FC<SelfMonitorChartProps> = ({
    title, icon, series, yAxisLabel, valueSuffix, plotLines, note, height = 220, loading, headerActions,
}) => {
    const chartSeries: EChartTimeSeries[] = useMemo(() => series.map(s => ({
        name: s.name,
        chartType: s.chartType ?? 'line',
        data: s.data,
        color: s.color,
        dashStyle: s.dashStyle,
        stack: s.stack,
        smooth: false,
    })), [series]);

    const thresholds: EChartThreshold[] = useMemo(() => (plotLines || []).map(pl => ({
        value: pl.value,
        label: pl.label,
        color: pl.color || DEFAULT_LIMIT_COLOR,
    })), [plotLines]);

    const yAxis: EChartYAxis[] = useMemo(() => [{ labels: true, name: yAxisLabel }], [yAxisLabel]);

    const tooltipFormatter = useCallback((params: any[]) => {
        if (!params.length) return '';
        const axisTime = Number(params[0].axisValue);
        const rows = chartSeries.flatMap(s => {
            if (s.data.length === 0) return [];
            const points = s.data as EChartPoint[];
            let point = points[0];
            for (const candidate of points.slice(1)) {
                if (Math.abs(candidate[0] - axisTime) < Math.abs(point[0] - axisTime)) point = candidate;
            }
            if (typeof point[1] !== 'number') return [];
            const matched = params.find(p => p.seriesName === s.name);
            const color = matched?.color ?? s.color ?? '#666';
            return [tooltipRow(color, s.name, `${point[1].toFixed(2)}${valueSuffix ? ` ${valueSuffix}` : ''}`)];
        });
        return rows.length > 0 ? wrapTooltip(params[0].axisValue, rows.join('')) : '';
    }, [chartSeries, valueSuffix]);

    const totalPoints = series.reduce((sum, s) => sum + s.data.length, 0);

    return (
        <div className="card self-monitor-chart">
            <div className="card-header d-flex align-items-center justify-content-between">
                <span><i className={`fal ${icon} fa-fw`} aria-hidden="true" /> {title}</span>
                {headerActions}
            </div>
            <div className="card-body">
                <EChartsWithData error={null} loading={loading} length={totalPoints}
                                 series={chartSeries} tooltipType={CHART_DATA_TYPE.GENERIC}
                                 tooltipFormatter={tooltipFormatter} thresholds={thresholds} yAxis={yAxis}
                                 legend={series.length > 1} height={height} brush={false} xExtent="data"/>
                {note && <div className="self-monitor-chart__note text-muted">{note}</div>}
            </div>
        </div>
    );
};

export default SelfMonitorChart;
